import {inject} from "@angular/core";
import {patchState, signalStore, withMethods, withState} from "@ngrx/signals";
import {withDevtools} from "@angular-architects/ngrx-toolkit";
import {ApiService} from "../../../shared/services/api.service";
import {finalize, take} from "rxjs";
import {NewsResource} from "../../../shared/types/generated/newsResource";
import {NewsCommentResource} from "../../../shared/types/generated/newsCommentResource";
import {NewsUserLikeResource} from "../../../shared/types/generated/newsUserLikeResource";
import {NewsUserReadResource} from "../../../shared/types/generated/newsUserReadResource";
import {NewsCreateRequest} from "../../../shared/types/generated/newsCreateRequest";
import {NewsUpdateRequest} from "../../../shared/types/generated/newsUpdateRequest";
import {NewsCommentCreateRequest} from "../../../shared/types/generated/newsCommentCreateRequest";
import {NewsFilterType} from "../../../shared/types/news-types/newsFilterType.type";
import {NewsStoreState} from "../../../shared/types/news-types/newsStoreState.interface";

const initialState: NewsStoreState = {
  newsListItems: [],
  selectedNewsListItem: null,
  filterGroupID: -1,
  filterUserID: 0,
  filterGroupNames: [],
  filterUserNames: [],
  searchString: '',
  filterType: 'all',
  limit: 10,
  endReached: false,
  isLoadingList: false,
  hasLoadedList: false,
};

function mergeNewsListItems(existingItems: NewsResource[], incomingItems: NewsResource[]): NewsResource[] {
  const mergedItems = [...existingItems];

  for (const incomingItem of incomingItems) {
    const existingIndex = mergedItems.findIndex((item) => item.id === incomingItem.id);

    if (existingIndex === -1) {
      mergedItems.push(incomingItem);
      continue;
    }

    mergedItems[existingIndex] = incomingItem;
  }

  return mergedItems;
}

export const NewsStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withDevtools('NewsStore'),
  withMethods((store) => {
    const api = inject(ApiService);

    return {
      resetStore() {
        patchState(store, initialState);
      },
      setSelectedNewsListItem(item: NewsResource | null) {
        patchState(store, {selectedNewsListItem: item});
      },
      setFilterType(filterType: NewsFilterType) {
        patchState(store, {filterType});
      },
      setSearchString(searchString: string) {
        patchState(store, {searchString});
      },
      setFilterGroupNames(names: string[]) {
        patchState(store, {filterGroupNames: names});
      },
      setFilterUserNames(names: string[]) {
        patchState(store, {filterUserNames: names});
      },
      setUserFilter(userIds: number[]) {
        if (!userIds.length) {
          return;
        }
        patchState(store, {
          filterUserID: userIds[0],
          filterGroupID: -1
        });
      },
      setGroupFilter(groupIds: number[]) {
        if (!groupIds.length) {
          return;
        }
        patchState(store, {
          filterGroupID: groupIds[0],
          filterUserID: 0
        });
      },
      clearFilter() {
        patchState(store, {
          filterGroupID: -1,
          filterUserID: 0,
          filterGroupNames: [],
          filterUserNames: [],
          filterType: 'all',
        });
      },
      search() {
        patchState(store, {
          newsListItems: [],
          endReached: false,
          hasLoadedList: false
        });
        this.loadList();
      },
      loadNextPage() {
        if (store.endReached()) {
          return;
        }
        this.loadList();
      },
      loadList() {
        patchState(store, {
          isLoadingList: true,
          hasLoadedList: false
        });

        api.getNewsPostsList(
          store.newsListItems().length,
          store.limit(),
          store.searchString() || null,
          store.filterType() === 'pinned' ? true : null,
          store.filterType() === 'scheduled' ? true : null,
          store.filterType() === 'expired' ? true : null,
          store.filterType() === 'favorite' ? true : null,
          store.filterType() === 'customuser' ? store.filterUserID() : null,
          store.filterType() === 'customgroup' ? store.filterGroupID() : null
        )
          .pipe(
            take(1),
            finalize(() => patchState(store, {isLoadingList: false}))
          )
          .subscribe({
            next: (newsListItems) => {
              patchState(store, (state) => ({
                ...state,
                newsListItems: mergeNewsListItems(state.newsListItems, newsListItems),
                endReached: newsListItems.length < state.limit,
                hasLoadedList: true
              }));
            },
            error: (response) => {
              console.error(response);
            }
          });
      },
      likeNewsPost(newsListItem: NewsResource, emoji: string | null) {
        const previousEmoji = newsListItem.emoji_selected;
        const previousLikeCount = newsListItem.like_count;
        const previousEmojis = [...(newsListItem.emojis ?? [])];
        const nextEmojis = (() => {
          if (previousEmoji === emoji) {
            return previousEmojis;
          }

          // User removes reaction: remove one matching previous emoji from the aggregate list.
          if (previousEmoji && !emoji) {
            const removeIndex = previousEmojis.findIndex((itemEmoji) => itemEmoji === previousEmoji);
            if (removeIndex === -1) {
              return previousEmojis;
            }
            return previousEmojis.filter((_, index) => index !== removeIndex);
          }

          // User adds first reaction: append to aggregate list.
          if (!previousEmoji && emoji) {
            return [...previousEmojis, emoji];
          }

          // User changes reaction: replace one matching previous reaction.
          if (previousEmoji && emoji) {
            const replaceIndex = previousEmojis.findIndex((itemEmoji) => itemEmoji === previousEmoji);
            if (replaceIndex === -1) {
              return [...previousEmojis, emoji];
            }
            return previousEmojis.map((itemEmoji, index) => index === replaceIndex ? emoji : itemEmoji);
          }

          return previousEmojis;
        })();
        const nextLikeCount = previousLikeCount + (
          previousEmoji === emoji
            ? 0
            : previousEmoji && !emoji
              ? -1
              : !previousEmoji && emoji
              ? 1
              : 0
        );

        patchState(store, (state) => ({
          ...state,
          newsListItems: state.newsListItems.map((item) => item.id === newsListItem.id
            ? {...item, emoji_selected: emoji, like_count: nextLikeCount, emojis: nextEmojis}
            : item
          ),
          selectedNewsListItem: state.selectedNewsListItem?.id === newsListItem.id
            ? {...state.selectedNewsListItem, emoji_selected: emoji, like_count: nextLikeCount, emojis: nextEmojis}
            : state.selectedNewsListItem
        }));

        api.setNewsPostLike(newsListItem.id, {emoji})
          .pipe(take(1))
          .subscribe({
            error: (response) => {
              patchState(store, (state) => ({
                ...state,
                newsListItems: state.newsListItems.map((item) => item.id === newsListItem.id
                  ? {...item, emoji_selected: previousEmoji, like_count: previousLikeCount, emojis: previousEmojis}
                  : item
                ),
                selectedNewsListItem: state.selectedNewsListItem?.id === newsListItem.id
                  ? {...state.selectedNewsListItem, emoji_selected: previousEmoji, like_count: previousLikeCount, emojis: previousEmojis}
                  : state.selectedNewsListItem
              }));
              console.error(response);
            }
          });
      },
      setPostPin(newsListItem: NewsResource, pin: boolean) {
        const previousPin = newsListItem.pinned;
        const previousNewsListItems = [...store.newsListItems()];
        const previousSelectedNewsListItem = store.selectedNewsListItem();

        const getCreatedAtTimestamp = (item: NewsResource): number => {
          const timestamp = new Date(item.created_at).getTime();
          return Number.isNaN(timestamp) ? 0 : timestamp;
        };

        const getReorderedList = (items: NewsResource[]): NewsResource[] => {
          const pinnedItems = items
            .filter((item) => item.pinned)
            .sort((a, b) => getCreatedAtTimestamp(b) - getCreatedAtTimestamp(a));
          const unpinnedItems = items.filter((item) => !item.pinned);

          if (pin) {
            return [...pinnedItems, ...unpinnedItems];
          }

          const movedItem = unpinnedItems.find((item) => item.id === newsListItem.id);
          if (!movedItem) {
            return [...pinnedItems, ...unpinnedItems];
          }

          const remainingUnpinned = unpinnedItems.filter((item) => item.id !== newsListItem.id);
          const movedItemCreatedAt = getCreatedAtTimestamp(movedItem);
          const insertIndex = remainingUnpinned.findIndex((item) => movedItemCreatedAt >= getCreatedAtTimestamp(item));

          if (insertIndex === -1) {
            return [...pinnedItems, ...remainingUnpinned, movedItem];
          }

          return [
            ...pinnedItems,
            ...remainingUnpinned.slice(0, insertIndex),
            movedItem,
            ...remainingUnpinned.slice(insertIndex)
          ];
        };

        patchState(store, (state) => ({
          ...state,
          newsListItems: (() => {
            const updatedItems = state.newsListItems.map((item) => item.id === newsListItem.id ? {...item, pinned: pin} : item);
            if (state.filterType === 'pinned' && !pin) {
              return updatedItems.filter((item) => item.id !== newsListItem.id);
            }
            return getReorderedList(updatedItems);
          })(),
          selectedNewsListItem: state.selectedNewsListItem?.id === newsListItem.id
            ? {...state.selectedNewsListItem, pinned: pin}
            : state.selectedNewsListItem
        }));

        api.setNewsPostPin(newsListItem.id, {pin})
          .pipe(take(1))
          .subscribe({
            error: (response) => {
              patchState(store, {
                newsListItems: previousNewsListItems,
                selectedNewsListItem: previousSelectedNewsListItem?.id === newsListItem.id
                  ? {...previousSelectedNewsListItem, pinned: previousPin}
                  : previousSelectedNewsListItem
              });
              console.error(response);
            }
          });
      },
      deleteNewsPost(postId: number, onSuccess?: () => void) {
        api.deleteNewsPost(postId)
          .pipe(take(1))
          .subscribe({
            next: () => {
              patchState(store, (state) => ({
                ...state,
                newsListItems: state.newsListItems.filter((item) => item.id !== postId),
                selectedNewsListItem: state.selectedNewsListItem?.id === postId ? null : state.selectedNewsListItem
              }));
              onSuccess?.();
            },
            error: (response) => {
              console.error(response);
            }
          });
      },
      loadNewsPostDetail(postId: number, onLoaded?: (item: NewsResource | null) => void) {
        api.getNewsPost(postId)
          .pipe(take(1))
          .subscribe({
            next: (newsPost) => {
              if (!newsPost) {
                onLoaded?.(null);
                return;
              }
              patchState(store, (state) => ({
                ...state,
                newsListItems: state.newsListItems.map((item) => item.id === newsPost.id ? newsPost : item),
                selectedNewsListItem: newsPost
              }));
              onLoaded?.(newsPost);
            },
            error: (response) => {
              console.error(response);
              onLoaded?.(null);
            }
          });
      },
      setNewsPostRead(postId: number) {
        const existingItem = store.newsListItems().find((item) => item.id === postId);
        const selectedItem = store.selectedNewsListItem();
        const previousListItemRead = existingItem?.read ?? null;
        const previousSelectedRead = selectedItem?.id === postId ? selectedItem.read : null;

        patchState(store, (state) => ({
          ...state,
          newsListItems: state.newsListItems.map((item) => item.id === postId ? {...item, read: true} : item),
          selectedNewsListItem: state.selectedNewsListItem?.id === postId
            ? {...state.selectedNewsListItem, read: true}
            : state.selectedNewsListItem
        }));

        api.setNewsPostRead(postId)
          .pipe(take(1))
          .subscribe({
            next: (updatedNewsPost) => {
              if (!updatedNewsPost) {
                return;
              }

              patchState(store, (state) => ({
                ...state,
                newsListItems: state.newsListItems.map((item) => item.id === postId ? updatedNewsPost : item),
                selectedNewsListItem: state.selectedNewsListItem?.id === postId
                  ? updatedNewsPost
                  : state.selectedNewsListItem
              }));
            },
            error: (response) => {
              patchState(store, (state) => ({
                ...state,
                newsListItems: state.newsListItems.map((item) => item.id === postId && previousListItemRead !== null ? {...item, read: previousListItemRead} : item),
                selectedNewsListItem: state.selectedNewsListItem?.id === postId && previousSelectedRead !== null
                  ? {...state.selectedNewsListItem, read: previousSelectedRead}
                  : state.selectedNewsListItem
              }));
              console.error(response);
            }
          });
      },
      setNewsPostFavorite(postId: number) {
        const previousListItemFavorite = store.newsListItems().find((item) => item.id === postId)?.favorite ?? null;
        const previousSelectedFavorite = store.selectedNewsListItem()?.id === postId
          ? (store.selectedNewsListItem()?.favorite ?? null)
          : null;

        patchState(store, (state) => ({
          ...state,
          newsListItems: state.newsListItems.map((item) => item.id === postId ? {...item, favorite: true} : item),
          selectedNewsListItem: state.selectedNewsListItem?.id === postId
            ? {...state.selectedNewsListItem, favorite: true}
            : state.selectedNewsListItem
        }));

        api.setNewsPostFavorite(postId, true)
          .pipe(take(1))
          .subscribe({
            error: (response) => {
              patchState(store, (state) => ({
                ...state,
                newsListItems: state.newsListItems.map((item) => item.id === postId && previousListItemFavorite !== null ? {...item, favorite: previousListItemFavorite} : item),
                selectedNewsListItem: state.selectedNewsListItem?.id === postId && previousSelectedFavorite !== null
                  ? {...state.selectedNewsListItem, favorite: previousSelectedFavorite}
                  : state.selectedNewsListItem
              }));
              console.error(response);
            }
          });
      },
      setNewsPostUnfavorite(postId: number) {
        const previousListItemFavorite = store.newsListItems().find((item) => item.id === postId)?.favorite ?? null;
        const previousSelectedFavorite = store.selectedNewsListItem()?.id === postId
          ? (store.selectedNewsListItem()?.favorite ?? null)
          : null;
        const previousNewsListItems = [...store.newsListItems()];

        patchState(store, (state) => ({
          ...state,
          newsListItems: state.filterType === 'favorite'
            ? state.newsListItems.filter((item) => item.id !== postId)
            : state.newsListItems.map((item) => item.id === postId ? {...item, favorite: false} : item),
          selectedNewsListItem: state.selectedNewsListItem?.id === postId
            ? {...state.selectedNewsListItem, favorite: false}
            : state.selectedNewsListItem
        }));

        api.setNewsPostFavorite(postId, false)
          .pipe(take(1))
          .subscribe({
            error: (response) => {
              patchState(store, (state) => ({
                ...state,
                newsListItems: state.filterType === 'favorite'
                  ? previousNewsListItems
                  : state.newsListItems.map((item) => item.id === postId && previousListItemFavorite !== null ? {...item, favorite: previousListItemFavorite} : item),
                selectedNewsListItem: state.selectedNewsListItem?.id === postId && previousSelectedFavorite !== null
                  ? {...state.selectedNewsListItem, favorite: previousSelectedFavorite}
                  : state.selectedNewsListItem
              }));
              console.error(response);
            }
          });
      },
      upsertNewsListItem(newsListItem: NewsResource | null) {
        if (!newsListItem) {
          return;
        }

        patchState(store, (state) => {
          const currentIndex = state.newsListItems.findIndex((item) => item.id === newsListItem.id);
          const nextItems = currentIndex !== -1
            ? state.newsListItems.map((item) => item.id === newsListItem.id ? newsListItem : item)
            : [newsListItem, ...state.newsListItems];

          return {
            ...state,
            newsListItems: nextItems,
            selectedNewsListItem: newsListItem
          };
        });
      },
      createPost(payload: NewsCreateRequest, onSuccess?: () => void) {
        api.createNewsPost(payload)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.search();
              onSuccess?.();
            },
            error: (response) => {
              console.error(response);
            }
          });
      },
      updatePost(postId: number, payload: NewsUpdateRequest, onSuccess?: () => void) {
        api.updateNewsPost(postId, payload)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.search();
              onSuccess?.();
            },
            error: (response) => {
              console.error(response);
            }
          });
      },
      getPostLikes(postId: number, skip?: number | null, limit?: number | null, search?: string | null) {
        return api.getNewsPostLikes(postId, skip, limit, search);
      },
      getPostReaders(postId: number, skip?: number | null, limit?: number | null, mode: 'read' | 'unread' | null = null, search?: string | null) {
        return api.getNewsPostReaders(postId, skip, limit, mode, search);
      },
      getPostRootComments(postId: number, after?: number | null, limit?: number | null) {
        return api.getNewsPostRootComments(postId, after, limit);
      },
      getPostCommentReplies(postId: number, commentId: number, after?: number | null, limit?: number | null) {
        return api.getNewsPostCommentReplies(postId, commentId, after, limit);
      },
      getPostCommentLikes(postId: number, commentId: number, skip?: number | null, limit?: number | null, search?: string | null) {
        return api.getNewsPostCommentLikes(postId, commentId, skip, limit, search);
      },
      createPostRootComment(postId: number, payload: NewsCommentCreateRequest) {
        return api.createNewsPostRootComment(postId, payload);
      },
      createPostCommentReply(postId: number, commentId: number, payload: NewsCommentCreateRequest) {
        return api.createNewsPostCommentReply(postId, commentId, payload);
      },
      updatePostComment(postId: number, commentId: number, payload: NewsCommentCreateRequest) {
        return api.updateNewsPostComment(postId, commentId, payload);
      },
      deletePostComment(postId: number, commentId: number) {
        return api.deleteNewsPostComment(postId, commentId);
      },
      setPostCommentLike(postId: number, commentId: number, like: boolean = true) {
        return api.setNewsPostCommentLike(postId, commentId, like);
      },
      applyCommentUpdateToSelectedPost(postId: number, commentDelta: number = 0) {
        patchState(store, (state) => ({
          ...state,
          newsListItems: state.newsListItems.map((item) => item.id === postId ? {...item, comment_count: Math.max(0, item.comment_count + commentDelta)} : item),
          selectedNewsListItem: state.selectedNewsListItem?.id === postId
            ? {...state.selectedNewsListItem, comment_count: Math.max(0, state.selectedNewsListItem.comment_count + commentDelta)}
            : state.selectedNewsListItem
        }));
      },
      replaceSelectedPost(post: NewsResource) {
        this.upsertNewsListItem(post);
      },
      // keep these typed helpers explicit for component imports and autocomplete
      _typedHelpers(_: {
        like: NewsUserLikeResource[];
        readers: NewsUserReadResource[];
        comments: NewsCommentResource[];
      }) {
        return _;
      }
    };
  })
);
